import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { INotification, INotificationSummary } from '../models/notification.model';
import { IApiResponse } from '../models';
import { environment } from '../../../environments/environment';
import { API } from '../constants/api-endpoints';

/** Full history is loaded so the notifications panel can filter locally between its "all"/"unread" tabs */
const NOTIFICATIONS_LOAD_LIMIT = 50;

interface IPermissionRequestUpdatedPayload {
  requestId: string;
  status: string;
  message: string;
  timestamp: string;
}

interface INewPermissionRequestPayload {
  requestId: string;
  userName: string;
  requestType: string;
  message: string;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class SignalRService {

  private readonly http = inject(HttpClient);

  private readonly HUB_URL = `${environment.apiBaseUrl.replace(/\/api\/?$/, '')}/hubs/notifications`;
  private hubConnection: signalR.HubConnection | null = null;

  notifications = signal<INotification[]>([]);
  summary = signal<INotificationSummary>({ total: 0, unread: 0 });
  isConnected = signal<boolean>(false);

  /**
   * Loads existing notifications from the backend so they survive a page refresh.
   * Merges by id with whatever is already in memory (e.g. a live SignalR push that
   * arrived before this resolved) instead of overwriting it.
   * Failures are swallowed — notification history is a non-critical enhancement,
   * and live SignalR delivery keeps working regardless.
   */
  async loadInitial(): Promise<void> {
    try {
      const params = new HttpParams()
        .set('unreadOnly', 'false')
        .set('limit', NOTIFICATIONS_LOAD_LIMIT);
      const response = await firstValueFrom(
        this.http.get<IApiResponse<INotification[]>>(`${environment.apiUrl}${API.NOTIFICATIONS.LIST}`, { params })
      );
      const fetched = response.success && response.data ? response.data : [];
      this.notifications.update(existing => this._mergeById(fetched, existing));
      this._recomputeSummary();
    } catch {
      // Notification history is a non-critical enhancement; live SignalR push still works if this fails.
    }
  }

  async connect(): Promise<void> {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    try {
      await this.hubConnection.start();
      this.isConnected.set(true);

      this.hubConnection.on('PermissionRequestUpdated', (payload: IPermissionRequestUpdatedPayload) => {
        const notification = this._mapPermissionRequestPayload(payload);
        this.notifications.update(list => [notification, ...list]);
        this.summary.update(s => ({
          total: s.total + 1,
          unread: s.unread + 1
        }));
      });

      this.hubConnection.on('NewPermissionRequest', (payload: INewPermissionRequestPayload) => {
        const notification = this._mapNewPermissionRequestPayload(payload);
        this.notifications.update(list => [notification, ...list]);
        this.summary.update(s => ({
          total: s.total + 1,
          unread: s.unread + 1
        }));
      });
    } catch {
      this.isConnected.set(false);
    }
  }

  async disconnect(): Promise<void> {
    await this.hubConnection?.stop();
    this.hubConnection = null;
    this.isConnected.set(false);
  }

  /**
   * Marks a single notification as read, both locally and on the server.
   * Applies the local update optimistically, then persists it via
   * `POST /api/notifications/{id}/read` and refreshes the list from the
   * backend so read-state stays in sync across sessions/devices.
   * @param notificationId - Id of the notification to mark as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const previousNotifications = this.notifications();
    const previousSummary = this.summary();
    let wasUnread = false;
    this.notifications.update(list =>
      list.map(n => {
        if (n.id !== notificationId || n.status === 'read') return n;
        wasUnread = true;
        return { ...n, status: 'read', readAt: new Date().toISOString() };
      })
    );
    if (wasUnread) {
      this.summary.update(s => ({ ...s, unread: Math.max(0, s.unread - 1) }));
    }
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}${API.NOTIFICATIONS.MARK_AS_READ(notificationId)}`, {})
      );
      await this.loadInitial();
    } catch (error) {
      this.notifications.set(previousNotifications);
      this.summary.set(previousSummary);
      throw error;
    }
  }

  /**
   * Marks every notification as read, both locally and on the server.
   * Applies the local update optimistically, then persists it via
   * `POST /api/notifications/read-all` and refreshes the list from the
   * backend so read-state stays in sync across sessions/devices.
   */
  async markAllAsRead(): Promise<void> {
    const previousNotifications = this.notifications();
    const previousSummary = this.summary();
    this.notifications.update(list =>
      list.map(n => n.status === 'unread' ? { ...n, status: 'read', readAt: new Date().toISOString() } : n)
    );
    this.summary.update(s => ({ ...s, unread: 0 }));
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}${API.NOTIFICATIONS.MARK_ALL_AS_READ}`, {})
      );
      await this.loadInitial();
    } catch (error) {
      this.notifications.set(previousNotifications);
      this.summary.set(previousSummary);
      throw error;
    }
  }

  private _mergeById(fetched: INotification[], existing: INotification[]): INotification[] {
    const byId = new Map<string, INotification>();
    [...fetched, ...existing].forEach(n => byId.set(n.id, n));
    return Array.from(byId.values())
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  private _recomputeSummary(): void {
    const list = this.notifications();
    this.summary.set({
      total: list.length,
      unread: list.filter(n => n.status === 'unread').length
    });
  }

  private _mapPermissionRequestPayload(payload: IPermissionRequestUpdatedPayload): INotification {
    const approved = payload.status === 'Approved';
    return {
      id: payload.requestId,
      type: approved ? 'workflow.approved' : 'workflow.rejected',
      title: approved ? 'تم اعتماد طلب الصلاحيات' : 'تم رفض طلب الصلاحيات',
      message: payload.message,
      priority: 'medium',
      status: 'unread',
      createdAt: payload.timestamp,
      relatedEntityId: payload.requestId,
      relatedEntityType: 'PermissionRequest'
    };
  }

  private _mapNewPermissionRequestPayload(payload: INewPermissionRequestPayload): INotification {
    return {
      id: payload.requestId,
      type: 'workflow.pending_review',
      title: 'طلب صلاحيات جديد',
      message: payload.message,
      priority: 'medium',
      status: 'unread',
      createdAt: payload.timestamp,
      relatedEntityId: payload.requestId,
      relatedEntityType: 'PermissionRequest'
    };
  }
}
